import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, DollarSign, TrendingUp, AlertTriangle, Loader2, MapPin, Mail, User, Download,
} from 'lucide-react';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';

/**
 * AdminPayouts
 * Read-only view of Stripe Transfer objects going to talent Connect accounts.
 * Answers "did Jane actually get paid?" in under 5 seconds without hopping
 * into the Stripe dashboard.
 */

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const formatUnixTsDate = (ts) => formatDateMMDDYYYY(new Date((ts || 0) * 1000));

const AdminPayouts = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState(null);

  const load = async (startingAfter = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '25');
      if (startingAfter) params.set('starting_after', startingAfter);
      const { data } = await api.get(`/admin/payouts?${params.toString()}`);
      setError(data.error || null);
      setHasMore(data.has_more);
      if (startingAfter) {
        setRows((prev) => [...prev, ...(data.transfers || [])]);
      } else {
        setRows(data.transfers || []);
      }
      const last = (data.transfers || []).slice(-1)[0];
      setCursor(last?.id || null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load payouts');
      toast.error('Could not load Stripe payouts');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const { data } = await api.get('/admin/payouts/summary');
      setSummary(data);
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadSummary();
  }, []);

  const exportCSV = () => {
    const header = ['Date', 'Talent', 'Email', 'State', 'Amount', 'Status', 'TransferID', 'Description'];
    const csv = [
      header.join(','),
      ...rows.map(r => [
        formatUnixTsDate(r.created_at),
        `"${(r.talent_name || '').replace(/"/g, '""')}"`,
        r.talent_email || '',
        r.talent_state || '',
        r.amount,
        r.status,
        r.id,
        `"${(r.description || '').replace(/"/g, '""')}"`,
      ].join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stripe_payouts_${formatDateMMDDYYYY(new Date()).replace(/\//g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="payouts-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>

        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-emerald-600" /> Talent Payouts
            </h1>
            <p className="text-sm text-slate-500">
              Live feed of Stripe Connect transfers. Read-only — initiate new payouts from the booking page.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCSV}
            disabled={rows.length === 0}
            data-testid="payouts-export-csv"
          >
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>

        {/* Summary */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Card>
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Last 30 days</p>
              <p className="text-2xl font-semibold">
                {summaryLoading ? <Loader2 className="w-5 h-5 animate-spin inline" /> : formatCurrency(summary?.total_amount)}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {summary?.count ?? 0} payouts
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Reversed</p>
              <p className="text-2xl font-semibold text-red-600">
                {summary?.reversed_count ?? 0}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                requires manual review
              </p>
            </CardContent>
          </Card>
          <Card className="sm:col-span-2">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Top states</p>
              {summary?.top_states?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {summary.top_states.map((s) => (
                    <Badge key={s.state} className="bg-slate-100 text-slate-800 border border-slate-200">
                      {s.state} · {formatCurrency(s.total)}
                    </Badge>
                  ))}
                </div>
              ) : <p className="text-xs text-slate-400">No payout activity yet</p>}
            </CardContent>
          </Card>
        </div>

        {error && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="payouts-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-medium">Stripe API issue</p>
              <p className="text-xs text-amber-800 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        <Card>
          <CardHeader><CardTitle className="text-base">Transfers</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading && rows.length === 0 ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : rows.length === 0 ? (
              <p className="text-center text-slate-500 text-sm py-10">No payouts in range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="px-4 py-2 font-medium">Date</th>
                      <th className="px-4 py-2 font-medium">Talent</th>
                      <th className="px-4 py-2 font-medium">Amount</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">Transfer ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`payout-row-${r.id}`}>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                          {formatUnixTsDate(r.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          {r.talent_name ? (
                            <div>
                              <p className="font-medium flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5 text-slate-400" />
                                {r.talent_name}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                                {r.talent_email && <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" />{r.talent_email}</span>}
                                {r.talent_state && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{r.talent_city ? `${r.talent_city}, ` : ''}{r.talent_state}</span>}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">Unidentified ({r.destination})</span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-emerald-700">{formatCurrency(r.amount)}</td>
                        <td className="px-4 py-3">
                          <Badge className={r.status === 'paid'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-red-50 text-red-700 border border-red-200'}>
                            <TrendingUp className="w-3 h-3 mr-0.5" /> {r.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">{r.id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {hasMore && (
                  <div className="p-3 flex justify-center">
                    <Button variant="outline" size="sm" onClick={() => load(cursor)} disabled={loading}>
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                      Load more
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPayouts;
